import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { MenuCategory } from '@planner/types'

export function useMenuCategories() {
  const [categories, setCategories] = useState<MenuCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchCategories = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data, error: fetchError } = await supabase
        .from('menu_categories')
        .select('*')
        .order('display_order', { ascending: true })

      if (fetchError) {
        setError(fetchError.message)
      } else {
        setCategories(data || [])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const createCategory = useCallback(async (
    name: string,
    description?: string,
    display_order?: number
  ): Promise<{ error: string | null; data?: MenuCategory }> => {
    try {
      // Sprawdź czy kategoria o tej nazwie już istnieje
      const { data: existing } = await supabase
        .from('menu_categories')
        .select('id')
        .eq('name', name)
        .single()

      if (existing) {
        return { error: 'Kategoria o tej nazwie już istnieje' }
      }

      // Jeśli nie podano display_order, znajdź maksymalną wartość i dodaj 1
      let order = display_order
      if (order === undefined) {
        const { data: maxOrder } = await supabase
          .from('menu_categories')
          .select('display_order')
          .order('display_order', { ascending: false })
          .limit(1)
          .single()

        order = maxOrder ? ((maxOrder as { display_order?: number }).display_order || 0) + 1 : 1
      }

      const { data, error: insertError } = await supabase
        .from('menu_categories')
        .insert([{
          name,
          description: description || null,
          display_order: order,
        }] as unknown as never)
        .select()
        .single()

      if (insertError) throw insertError

      await fetchCategories()
      return { error: null, data: data as MenuCategory }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [fetchCategories])

  const updateCategory = useCallback(async (
    id: string,
    updates: Partial<MenuCategory>
  ): Promise<{ error: string | null }> => {
    try {
      // Jeśli zmieniana jest nazwa, sprawdź czy nie koliduje z inną kategorią
      if (updates.name) {
        const { data: existing } = await supabase
          .from('menu_categories')
          .select('id')
          .eq('name', updates.name)
          .neq('id', id)
          .single()

        if (existing) {
          return { error: 'Kategoria o tej nazwie już istnieje' }
        }
      }

      const { error: updateError } = await supabase
        .from('menu_categories')
        .update(updates as unknown as never)
        .eq('id', id)

      if (updateError) throw updateError

      await fetchCategories()
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [fetchCategories])

  const deleteCategory = useCallback(async (
    id: string
  ): Promise<{ error: string | null }> => {
    try {
      // Sprawdź czy kategoria jest używana w daniach
      const { data: itemsInCategory } = await supabase
        .from('menu_item_categories')
        .select('id')
        .eq('menu_category_id', id)
        .limit(1)

      if (itemsInCategory && itemsInCategory.length > 0) {
        return { error: 'Nie można usunąć kategorii, ponieważ jest przypisana do co najmniej jednego dania' }
      }

      // Sprawdź czy kategoria jest używana w pakietach
      const { data: packagesWithCategory } = await supabase
        .from('menu_package_categories')
        .select('id')
        .eq('menu_category_id', id)
        .limit(1)

      if (packagesWithCategory && packagesWithCategory.length > 0) {
        return { error: 'Nie można usunąć kategorii, ponieważ jest używana w co najmniej jednym pakiecie' }
      }

      const { error: deleteError } = await supabase
        .from('menu_categories')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      await fetchCategories()
      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Wystąpił nieoczekiwany błąd' }
    }
  }, [fetchCategories])

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  }
}

